import { api, Api } from "@api/base-api-request.ts";
import {
  ScreenerTokenRaw,
  ScreenerTokensResponse,
} from "@api/token-screener/types.ts";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";

const QUERY_KEY = "screener-tokens";
const QUERY_KEY_INFINITE = "screener-tokens-infinite";

export type SortFieldKey =
  | "age"
  | "txns"
  | "makers"
  | "holders"
  | "volume"
  | "liquidity"
  | "price"
  | "marketCap"
  | "priceChange1Hour"
  | "priceChange24Hours";

export type ScreenerTokensParams = {
  tokenId?: string;
  network?: string;
  limit?: number;
  page?: number;
  phrase?: string | null;
  filters?: {
    picture?: boolean | null;
    website?: boolean | null;
    twitter?: boolean | null;
    telegram?: boolean | null;
    price?: {
      min?: number | null;
      max?: number | null;
    };
    age?: {
      min?: number | null;
      max?: number | null;
    };
    txns?: {
      min?: number | null;
      max?: number | null;
    };
    makers?: {
      min?: number | null;
      max?: number | null;
    };
    holders?: {
      min?: number | null;
      max?: number | null;
    };
    volume?: {
      min?: number | null;
      max?: number | null;
    };
    liquidity?: {
      min?: number | null;
      max?: number | null;
    };
    marketCap?: {
      min?: number | null;
      max?: number | null;
    };
    priceChange1Hour?: {
      min?: number | null;
      max?: number | null;
    };
    priceChange24Hours?: {
      min?: number | null;
      max?: number | null;
    };
    tokens?: string[] | null;
    exchange?: {
      uniswap?: boolean | null;
      raydium?: boolean | null;
      orca?: boolean | null;
      meteora?: boolean | null;
      pump?: boolean | null;
      pumpAmm?: boolean | null;
      launchlab?: boolean | null;
      boop?: boolean | null;
      fluxbeam?: boolean | null;
    };
    address?: string | null;
    sort?: {
      trending?: boolean | null;
      watchlist?: number | null;
      age?: null | "asc" | "desc";
      txns?: null | "asc" | "desc";
      makers?: null | "asc" | "desc";
      holders?: null | "asc" | "desc";
      volume?: null | "asc" | "desc";
      liquidity?: null | "asc" | "desc";
      price?: null | "asc" | "desc";
      marketCap?: null | "asc" | "desc";
      priceChange1Hour?: null | "asc" | "desc";
      priceChange24Hours?: null | "asc" | "desc";
    };
  };
};

export type FiltersSort = {
  trending?: boolean | null;
  watchlist?: number | null;
  age?: "asc" | "desc" | null;
  txns?: "asc" | "desc" | null;
  makers?: "asc" | "desc" | null;
  holders?: "asc" | "desc" | null;
  volume?: "asc" | "desc" | null;
  liquidity?: "asc" | "desc" | null;
  price?: "asc" | "desc" | null;
  marketCap?: "asc" | "desc" | null;
  priceChange1Hour?: "asc" | "desc" | null;
  priceChange24Hours?: "asc" | "desc" | null;
};

const transformParams = (params: ScreenerTokensParams) => {
  const result: Record<string, string | number> = {};

  if (params.limit != null) {
    result.limit = params.limit;
  }

  if (params.page != null) {
    result.page = params.page;
  }

  if (params.phrase) {
    result.phrase = params.phrase;
  }

  if (params.network != null) {
    result.network = params.network;
  }

  if (params.filters) {
    result.filters = JSON.stringify(params.filters);
  }

  return result;
};

const fetchScreenerTokens = async (
  api: Api,
  params: ScreenerTokensParams,
): Promise<ScreenerTokensResponse> =>
  api
    .get("tokenScreener", {
      searchParams: transformParams(params),
    })
    .json();

const fetchScreenerToken = async (
  api: Api,
  params: ScreenerTokensParams,
): Promise<ScreenerTokensResponse> =>
  api
    .get(`tokenScreener/${params.tokenId}`, {
      searchParams: transformParams(params),
    })
    .json();

const defaultSelector = (
  data: ScreenerTokensResponse,
): ScreenerTokensResponse => data;

const useInfiniteGetScreenerTokens = (params: ScreenerTokensParams) =>
  useInfiniteQuery<ScreenerTokensResponse>({
    queryKey: [QUERY_KEY_INFINITE, params],
    queryFn: async ({ pageParam = 0 }) =>
      fetchScreenerTokens(api, { ...params, page: pageParam as number }),
    getNextPageParam: (lastPage) => {
      if (lastPage.hasNextPage) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage, pages) => {
      if (pages[pages.length - 1].page === 0) {
        return undefined;
      }
      return firstPage.page - 1;
    },
    placeholderData: (data) => data,
    initialPageParam: 0,
  });

const useGetScreenerTokens = <TData = ScreenerTokensResponse>(
  params: ScreenerTokensParams,
  selector: (data: ScreenerTokensResponse) => TData = defaultSelector as (
    data: ScreenerTokensResponse,
  ) => TData,
) =>
  useQuery<ScreenerTokensResponse, Error, TData>({
    queryKey: [QUERY_KEY, params],
    queryFn: async () => fetchScreenerTokens(api, params),
    placeholderData: (data) => data,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    select: useCallback(selector, [selector]),
  });

const useGetScreenerToken = <TData = ScreenerTokensResponse>(
  params: ScreenerTokensParams,
  selector: (data: ScreenerTokensResponse) => TData = defaultSelector as (
    data: ScreenerTokensResponse,
  ) => TData,
) =>
  useQuery<ScreenerTokensResponse, Error, TData>({
    queryKey: [QUERY_KEY, params],
    queryFn: async () => fetchScreenerToken(api, params),
    select: selector,
  });

const useGetTokenAddressByName = (id: string) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [QUERY_KEY, "address", id],
    queryFn: async () => {
      const tokens = queryClient.getQueryData<ScreenerTokenRaw[]>([QUERY_KEY]);
      return tokens ?? [];
    },
    select: (tokens: ScreenerTokenRaw[]) =>
      tokens.find((token) => token.pair?.token0Data?.id === id)?.address,
    enabled: Boolean(queryClient.getQueryData([QUERY_KEY])),
  });
};

export {
  fetchScreenerTokens,
  useGetScreenerTokens,
  useGetScreenerToken,
  fetchScreenerToken,
  useGetTokenAddressByName,
  useInfiniteGetScreenerTokens,
  QUERY_KEY_INFINITE as SCREENER_QUERY_KEY_INFINITE,
  QUERY_KEY as SCREENER_QUERY_KEY,
};
